import { useState, Fragment } from 'react'
import { SkeletonTable } from '@/components/common/Skeleton'
import { Link } from 'react-router-dom'
import { AssetTypeBadge, QuantumSafeBadge, ModalityBadge } from '@/components/common/StatusBadge'
import type { FindingEntry, Severity, Modality, AssetLifecycle } from '@/api/types'
import { RiskBadge as QuantumRiskBadge } from '@/pages/Packages'
import { formatVuln } from '@/lib/vuln-labels'

// ── Risk level badge ──────────────────────────────────────────────────────────

const riskColors: Record<Severity, string> = {
  critical: 'bg-error/15 text-error dark:bg-error/25 dark:text-red-300',
  high:     'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  medium:   'bg-warning/15 text-yellow-800 dark:bg-warning/20 dark:text-yellow-300',
  low:      'bg-success/10 text-green-800 dark:bg-success/15 dark:text-green-300',
  info:     'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark',
}

// Severity arrives from the scanner, so an unrecognised value is possible.
// Fall back to the neutral style rather than emitting `undefined` as a class.
const neutralBadge = 'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark'

function RiskBadge({ level }: { level: Severity }) {
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${riskColors[level] ?? neutralBadge}`}>
      {level}
    </span>
  )
}

/**
 * Explains why a quantum assessment is provisional. The scanner reports the
 * gap as a bare token; on its own "certificate_encrypted" reads like an error
 * rather than the expected consequence of watching TLS 1.3 passively.
 */
function describeEvidenceGap(gap: string): string {
  switch (gap) {
    case 'certificate_encrypted':
      return 'The certificate was encrypted in the captured handshake (TLS 1.3 and QUIC encrypt it).'
    case 'certificate_not_observed':
      return 'No certificate appeared in the captured traffic.'
    default:
      return `Missing input: ${gap}.`
  }
}

/** Renders "peer=client connections=3" as "3 connections". */
function formatPeerContext(ctx?: string): string {
  const n = ctx?.match(/connections=(\d+)/)?.[1]
  if (!n) return ''
  return `${n} connection${n === '1' ? '' : 's'}`
}

// ── Detail of crypto properties ───────────────────────────────────────────────

export function CryptoPropsDetail({ finding }: { finding: FindingEntry }) {
  const cp = finding.crypto_properties
  if (!cp) return <p className="text-xs text-subtle-light dark:text-subtle-dark">No crypto properties available.</p>

  const rows: Array<[string, string | number | undefined | null]> = []

  if (cp.algorithmProperties) {
    const ap = cp.algorithmProperties
    if (ap.primitive) rows.push(['Primitive', ap.primitive])
    if (ap.parameterSetIdentifier) rows.push(['Key / Param Size', ap.parameterSetIdentifier])
    if (ap.mode) rows.push(['Mode', ap.mode])
    if (ap.padding) rows.push(['Padding', ap.padding])
    if (ap.cryptoFunctions?.length) rows.push(['Functions', ap.cryptoFunctions.join(', ')])
    if (ap.nistQuantumSecurityLevel !== undefined)
      rows.push(['NIST Quantum Level', ap.nistQuantumSecurityLevel])
    if (ap.classicalSecurityLevel != null)
      rows.push(['Classical Security', `${ap.classicalSecurityLevel} bits`])
  }

  if (cp.certificateProperties) {
    const cert = cp.certificateProperties
    if (cert.subjectName) rows.push(['Subject', cert.subjectName])
    if (cert.issuerName) rows.push(['Issuer', cert.issuerName])
    const validFrom = cert.notValidBefore ?? cert.notBefore
    const validUntil = cert.notValidAfter ?? cert.notAfter
    if (validFrom) rows.push(['Valid From', new Date(validFrom).toLocaleDateString()])
    if (validUntil) rows.push(['Valid Until', new Date(validUntil).toLocaleDateString()])
    if (cert.daysUntilExpiry != null) rows.push(['Days Until Expiry', cert.daysUntilExpiry])
    if (cert.publicKeyAlgorithm) rows.push(['Key Algorithm', cert.publicKeyAlgorithm])
    if (cert.signatureAlgorithmRef) rows.push(['Signature Alg', cert.signatureAlgorithmRef])
    if (cert.publicKeyBits) rows.push(['Key Size', `${cert.publicKeyBits} bits`])
    if (cert.serialNumber) rows.push(['Serial', cert.serialNumber])
    if (cert.isCA) rows.push(['CA', 'Yes'])
    if (cert._qtz?.isSelfSigned) rows.push(['Self-signed', 'Yes'])
    if (cert._qtz?.hostnameMismatch) rows.push(['Hostname Mismatch', 'Yes'])
    if (cert.fingerprint?.content)
      rows.push([
        `Fingerprint (${cert.fingerprint.alg ?? 'SHA'})`,
        cert.fingerprint.content.slice(0, 16) + '…',
      ])
  }

  if (cp.keyProperties) {
    const kp = cp.keyProperties
    if (kp.keySize) rows.push(['Key Size', `${kp.keySize} bits`])
    if (kp.format) rows.push(['Format', kp.format])
  }

  if (cp.relatedCryptoMaterialProperties) {
    const rcm = cp.relatedCryptoMaterialProperties
    if (rcm.type) rows.push(['Key Type', rcm.type])
    if (rcm.size != null) rows.push(['Key Size', `${rcm.size} bits`])
    if (rcm.state) rows.push(['State', rcm.state])
    if (rcm.format) rows.push(['Format', rcm.format])
    if (rcm.algorithmRef) rows.push(['Algorithm Ref', rcm.algorithmRef])
    if (rcm.creationDate) rows.push(['Created', new Date(rcm.creationDate).toLocaleDateString()])
    if (rcm.activationDate) rows.push(['Activated', new Date(rcm.activationDate).toLocaleDateString()])
    if (rcm.expirationDate) rows.push(['Expires', new Date(rcm.expirationDate).toLocaleDateString()])
    if (rcm.securedBy?.mechanism) rows.push(['Protected By', rcm.securedBy.mechanism])
  }

  if (cp.protocolProperties) {
    const pp = cp.protocolProperties
    if (pp.tlsGrade) rows.push(['TLS Grade', pp.tlsGrade])
    if (pp.quantumGrade) rows.push(['Quantum Grade', pp.quantumGrade])
    if (pp.quantumScore != null) rows.push(['Quantum Score', pp.quantumScore])
    if (pp.keyExchange) rows.push(['Key Exchange', pp.keyExchange])
    if (pp.keyExchangeType) rows.push(['Key Exchange Type', pp.keyExchangeType])
    if (pp.cipherSuite) rows.push(['Cipher Suite', pp.cipherSuite])
    if (pp.forwardSecrecy != null) rows.push(['Forward Secrecy', pp.forwardSecrecy ? 'Yes' : 'No'])
    if (pp.cnsaCompliant != null) rows.push(['CNSA Compliant', pp.cnsaCompliant ? 'Yes' : 'No'])
    if (pp.hndlRisk != null) rows.push(['HNDL Risk', pp.hndlRisk ? 'Yes' : 'No'])
    if (pp.ocspStatus) rows.push(['OCSP Status', pp.ocspStatus])
    if (pp.ocspStapled != null) rows.push(['OCSP Stapled', pp.ocspStapled ? 'Yes' : 'No'])
    if (pp.clientPqcGroups?.length) rows.push(['Client PQC Groups', pp.clientPqcGroups.join(', ')])
    // SSH findings use the same protocolProperties block.
    if (pp.serverVersion) rows.push(['Server Version', pp.serverVersion])
    if (pp.hostKeyAlgo) rows.push(['Host Key Algorithm', pp.hostKeyAlgo])
    if (pp.negotiatedKEX) rows.push(['Negotiated KEX', pp.negotiatedKEX])
    if (pp.pqcKEXSupport != null) rows.push(['PQC KEX Support', pp.pqcKEXSupport ? 'Yes' : 'No'])
    if (pp.weakKEXAccepted != null) rows.push(['Weak KEX Accepted', pp.weakKEXAccepted ? 'Yes' : 'No'])
    if (pp.weakMACAccepted != null) rows.push(['Weak MAC Accepted', pp.weakMACAccepted ? 'Yes' : 'No'])
  }

  if (cp.oid) rows.push(['OID', cp.oid])

  const cert = cp.certificateProperties
  if (cert?.isPQCSigned) rows.push(['PQC Signed', cert.signatureAlgPQC || 'Yes'])
  if (cert?.isCompositePQC) rows.push(['Composite PQC', cert.compositeAlg || 'Yes'])

  const live = cp._qtz
  // A vulnerability row is a finding, not a negotiated protocol: it has no
  // cipher suite or TLS version to describe, so without this block the rows
  // above produce nothing and the panel opens empty. What it does know is the
  // ID, what the detector matched, and which protocol component it applies to.
  const vuln = cp._qtz?.vulnId
    ? { ...formatVuln(cp._qtz.vulnId), id: cp._qtz.vulnId }
    : null
  if (vuln) {
    if (cp._qtz?.category) rows.push(['Category', cp._qtz.category])
    if (cp._qtz?.scanTarget) rows.push(['Scan Target', cp._qtz.scanTarget])
    if (cp._qtz?.detectionContext) rows.push(['Detected', cp._qtz.detectionContext])
    if (cp._qtz?.affectsRef) rows.push(['Affects Component', cp._qtz.affectsRef])
  }
  const gaps = cp.protocolProperties?.evidenceGaps ?? []
  const pqcDeclined = cp.protocolProperties?.pqcDeclinedByServer === true
  const acceptedSuites = cp.protocolProperties?.acceptedCipherSuites ?? []
  const acceptedGroups = cp.protocolProperties?.acceptedGroups ?? []

  // Client peers come through as evidence occurrences tagged "peer=client":
  // they are observed source addresses with connection counts, not file
  // locations, so they are listed apart from the capture/live occurrences.
  const occurrences = finding.evidence?.occurrences ?? []
  const clientPeers = occurrences.filter((ev) => ev.additional_context?.startsWith('peer=client'))
  const whereSeen = occurrences.filter((ev) => !ev.additional_context?.startsWith('peer=client'))

  return (
    <div className="space-y-3">
      {vuln && (
        <div className="text-xs rounded px-2 py-1.5 bg-red-50 dark:bg-red-900/20 text-error">
          <span className="font-medium">{vuln.label}</span>
          {vuln.label !== vuln.id && (
            <span className="ml-1 font-mono text-[10px] opacity-70">({vuln.id})</span>
          )}
          {vuln.description && <p className="text-[11px] text-error/80 mt-0.5">{vuln.description}</p>}
        </div>
      )}

      {rows.length > 0 && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {rows.map(([label, val]) => (
            <div key={label} className="contents">
              <dt className="text-xs text-subtle-light dark:text-subtle-dark">{label}</dt>
              <dd className="text-xs font-mono text-text-light dark:text-text-dark">{val ?? '—'}</dd>
            </div>
          ))}
        </dl>
      )}

      {finding.remediation_deadline && (
        <div>
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
            finding.remediation_deadline === 'immediate'
              ? 'bg-red-100 dark:bg-red-900/30 text-error'
              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
          }`}>
            <span className="material-icons text-xs">schedule</span>
            {finding.remediation_deadline === 'immediate'
              ? 'Remediate immediately'
              : `Remediate by ${finding.remediation_deadline}`}
          </span>
        </div>
      )}

      {cp.protocolProperties?.cipherSuites && cp.protocolProperties.cipherSuites.length > 0 && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">
            Cipher Suites ({cp.protocolProperties.cipherSuites.length})
          </p>
          <ul className="space-y-1">
            {cp.protocolProperties.cipherSuites.map((cs, i) => (
              <li key={i} className="text-xs font-mono bg-background-light dark:bg-background-dark rounded px-2 py-1.5">
                <span className="text-text-light dark:text-text-dark">{cs.name ?? '—'}</span>
                {cs.tlsGroups && cs.tlsGroups.length > 0 && (
                  <span className="block text-subtle-light dark:text-subtle-dark mt-0.5">
                    Groups: {cs.tlsGroups.join(', ')}
                  </span>
                )}
                {cs.algorithms && cs.algorithms.length > 0 && (
                  <span className="block text-subtle-light dark:text-subtle-dark mt-0.5">
                    Algorithms: {cs.algorithms.join(', ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* What the server will accept, as opposed to what it chose above. Kept
          as its own block: folded into "Cipher Suites" it would read as more
          of what was negotiated, which is the opposite of the point — a server
          can negotiate AES-GCM and still accept RC4 from a client that asks. */}
      {(acceptedSuites.length > 0 || acceptedGroups.length > 0) && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">
            Also accepted (not negotiated here)
          </p>
          {acceptedSuites.length > 0 ? (
            <div className="flex flex-wrap gap-1 mb-1.5">
              {acceptedSuites.map((cs) => (
                <span
                  key={cs}
                  className="text-xs font-mono bg-background-light dark:bg-background-dark rounded px-1.5 py-0.5 text-text-light dark:text-text-dark"
                >
                  {cs}
                </span>
              ))}
            </div>
          ) : null}
          {acceptedGroups.length > 0 ? (
            <p className="text-xs text-subtle-light dark:text-subtle-dark">
              Key-exchange groups: {acceptedGroups.join(', ')}
            </p>
          ) : null}
        </div>
      )}

      {cert?.certificateExtensions && cert.certificateExtensions.length > 0 && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">
            Certificate Extensions ({cert.certificateExtensions.length})
          </p>
          <ul className="space-y-0.5">
            {cert.certificateExtensions.map((ext, i) => (
              <li key={i} className="text-xs bg-background-light dark:bg-background-dark rounded px-2 py-1 text-text-light dark:text-text-dark">
                <span className="font-medium">{ext.commonExtensionName}</span>
                {ext.commonExtensionValue && (
                  <span className="text-subtle-light dark:text-subtle-dark ml-1">{ext.commonExtensionValue}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {cert?.subjectAltNames && cert.subjectAltNames.length > 0 && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">
            Subject Alt Names ({cert.subjectAltNames.length})
          </p>
          <div className="flex flex-wrap gap-1">
            {cert.subjectAltNames.map((san, i) => (
              <span key={i} className="text-xs font-mono bg-background-light dark:bg-background-dark rounded px-2 py-1">{san}</span>
            ))}
          </div>
        </div>
      )}

      {cp.protocolProperties?.tlsVersions && cp.protocolProperties.tlsVersions.length > 0 && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">TLS Versions</p>
          <div className="flex flex-wrap gap-1">
            {cp.protocolProperties.tlsVersions.map((v, i) => (
              <span key={i} className="text-xs font-mono bg-background-light dark:bg-background-dark rounded px-2 py-1">{v}</span>
            ))}
          </div>
        </div>
      )}

      {cp.protocolProperties?.vulnerabilities && cp.protocolProperties.vulnerabilities.length > 0 && (
        <div>
          <p className="text-xs font-medium text-error mb-1">
            Vulnerabilities ({cp.protocolProperties.vulnerabilities.length})
          </p>
          <ul className="space-y-0.5">
            {cp.protocolProperties.vulnerabilities.map((v, i) => {
              const info = formatVuln(v)
              return (
                <li
                  key={i}
                  className="text-xs bg-red-50 dark:bg-red-900/20 text-error rounded px-2 py-1"
                  title={info.description || v}
                >
                  <span className="font-medium">{info.label}</span>
                  {info.label !== v && (
                    <span className="ml-1 font-mono text-[10px] opacity-70">({v})</span>
                  )}
                  {info.description && (
                    <p className="text-[11px] text-error/80 mt-0.5">{info.description}</p>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {cp.protocolProperties?.recommendations && cp.protocolProperties.recommendations.length > 0 && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">
            Recommendations ({cp.protocolProperties.recommendations.length})
          </p>
          <ul className="space-y-0.5">
            {cp.protocolProperties.recommendations.map((r, i) => (
              <li key={i} className="text-xs bg-background-light dark:bg-background-dark rounded px-2 py-1.5 text-text-light dark:text-text-dark">{r}</li>
            ))}
          </ul>
        </div>
      )}

      {pqcDeclined && (
        <div className="flex items-start gap-1.5 text-xs rounded px-2 py-1.5 bg-warning/10 dark:bg-warning/15 text-yellow-800 dark:text-yellow-300">
          <span className="material-icons text-xs mt-0.5">report_problem</span>
          <span>
            <span className="font-medium">Server declined post-quantum key exchange.</span>{' '}
            The client offered a PQC or hybrid group and the server negotiated a classical
            one instead — a server configuration gap, not a client limitation.
          </span>
        </div>
      )}

      {gaps.length > 0 && (
        <div className="flex items-start gap-1.5 text-xs rounded px-2 py-1.5 bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark">
          <span className="material-icons text-xs mt-0.5">visibility_off</span>
          <span>
            <span className="font-medium">Assessed on partial evidence.</span>{' '}
            {gaps.map(describeEvidenceGap).join(' ')}{' '}
            Re-run the capture scan with <span className="font-mono">probe_certs</span> to
            complete it.
          </span>
        </div>
      )}

      {live?.status && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">
            Live monitor
          </p>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className={`px-2 py-0.5 font-medium rounded-full capitalize ${
              live.status === 'stale'
                ? 'bg-warning/15 text-yellow-800 dark:bg-warning/20 dark:text-yellow-300'
                : 'bg-success/10 text-green-800 dark:bg-success/15 dark:text-green-300'
            }`}>
              {live.status}
            </span>
            {live.firstSeen && (
              <span className="text-subtle-light dark:text-subtle-dark">
                First seen {new Date(live.firstSeen).toLocaleString()}
              </span>
            )}
            {live.lastSeen && (
              <span className="text-subtle-light dark:text-subtle-dark">
                · Last seen {new Date(live.lastSeen).toLocaleString()}
              </span>
            )}
            {live.observations != null && (
              <span className="text-subtle-light dark:text-subtle-dark">
                · {live.observations} observation{live.observations === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
      )}

      {clientPeers.length > 0 && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">
            Clients observed ({clientPeers.length})
          </p>
          <ul className="space-y-0.5">
            {clientPeers.map((ev, i) => (
              <li key={i} className="text-xs font-mono bg-background-light dark:bg-background-dark rounded px-2 py-1">
                <span className="text-primary">
                  {ev.location === '*' ? 'other clients' : ev.location}
                </span>
                <span className="text-subtle-light dark:text-subtle-dark ml-1">
                  {formatPeerContext(ev.additional_context)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {whereSeen.length > 0 && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">
            Evidence ({whereSeen.length})
          </p>
          <ul className="space-y-1">
            {whereSeen.map((ev, i) => (
              <li
                key={i}
                className="text-xs font-mono bg-background-light dark:bg-background-dark rounded px-2 py-1.5"
              >
                <span className="text-primary">{ev.location}</span>
                {ev.line != null && (
                  <span className="text-subtle-light dark:text-subtle-dark ml-1">:{ev.line}</span>
                )}
                {ev.additional_context && (
                  <span className="block text-subtle-light dark:text-subtle-dark mt-0.5 break-all">
                    {ev.additional_context}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── Summary stats ─────────────────────────────────────────────────────────────

export function FindingsSummaryBar({
  total,
  quantumSafe,
  quantumUnsafe,
  byRisk,
  byLifecycle,
}: {
  total: number
  quantumSafe: number
  quantumUnsafe: number
  byRisk: Partial<Record<Severity, number>>
  /**
   * Present only once a live monitor has marked something stale. Absent means
   * no live monitoring here, which is why the tile is conditional rather than
   * showing a permanent zero.
   */
  byLifecycle?: Partial<Record<AssetLifecycle, number>>
}) {
  const safePercent = total > 0 ? Math.round((quantumSafe / total) * 100) : 0
  const stale = byLifecycle?.stale ?? 0

  return (
    <div className={`grid grid-cols-2 gap-4 ${stale > 0 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
        <p className="label-sm text-subtle-light dark:text-subtle-dark">Total Assets</p>
        <p className="text-2xl font-bold tracking-display text-text-light dark:text-text-dark mt-1">{total}</p>
      </div>
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
        <p className="label-sm text-subtle-light dark:text-subtle-dark">Quantum Safe</p>
        <p className="text-2xl font-bold tracking-display text-success mt-1">
          {quantumSafe}
          <span className="text-sm font-normal text-subtle-light dark:text-subtle-dark ml-1">
            ({safePercent}%)
          </span>
        </p>
      </div>
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
        <p className="label-sm text-subtle-light dark:text-subtle-dark">At Risk</p>
        <p className="text-2xl font-bold tracking-display text-error mt-1">{quantumUnsafe}</p>
      </div>
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
        <p className="label-sm text-subtle-light dark:text-subtle-dark">High / Critical</p>
        <p className="text-2xl font-bold tracking-display text-warning mt-1">
          {(byRisk.high ?? 0) + (byRisk.critical ?? 0)}
        </p>
      </div>
      {stale > 0 && (
        <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
          <p className="label-sm text-subtle-light dark:text-subtle-dark" title="Reported by a live monitor but not seen recently">
            Stale
          </p>
          <p className="text-2xl font-bold tracking-display text-warning mt-1">{stale}</p>
        </div>
      )}
    </div>
  )
}

// ── Main table ────────────────────────────────────────────────────────────────

interface CryptoAssetsTableProps {
  findings: FindingEntry[]
  showScanColumn?: boolean
  showSourceColumn?: boolean
  sourceMap?: Record<string, string>
  isLoading?: boolean
  emptyMessage?: string
}

export function CryptoAssetsTable({
  findings,
  showScanColumn = false,
  showSourceColumn = false,
  sourceMap = {},
  isLoading = false,
  emptyMessage = 'No assets found.',
}: CryptoAssetsTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const toggle = (id: string) => setExpandedId((prev) => (prev === id ? null : id))

  return (
    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl overflow-hidden shadow-ambient">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" id="assets-table">
          <thead>
            <tr className="border-b border-border-light dark:border-border-dark">
              <th className="w-8 px-3 py-3" />
              <th className="px-4 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                Name
              </th>
              <th className="px-4 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                Type
              </th>
              <th className="px-4 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                Severity
              </th>
              <th className="px-4 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                Quantum Risk
              </th>
              <th className="px-4 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                Quantum Safety
              </th>
              <th className="px-4 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                Modality
              </th>
              {showSourceColumn && (
                <th className="px-4 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Source
                </th>
              )}
              {showScanColumn && (
                <th className="px-4 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Scan
                </th>
              )}
              <th className="px-4 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                Evidence
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8 + (showSourceColumn ? 1 : 0) + (showScanColumn ? 1 : 0)} className="p-0">
                  <SkeletonTable rows={5} cols={8 + (showSourceColumn ? 1 : 0) + (showScanColumn ? 1 : 0)} />
                </td>
              </tr>
            ) : findings.length === 0 ? (
              <tr>
                <td
                  colSpan={8 + (showSourceColumn ? 1 : 0) + (showScanColumn ? 1 : 0)}
                  className="px-4 py-16 text-center"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-container-light dark:bg-container-dark flex items-center justify-center">
                      <span className="material-icons text-3xl text-subtle-light dark:text-subtle-dark">key</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-text-light dark:text-text-dark">No assets found</p>
                      <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1">{emptyMessage}</p>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              findings.map((finding, i) => {
                const rowAccent =
                  finding.quantum_risk === 'vulnerable' ? 'border-l-2 border-error'
                  : finding.quantum_risk === 'partial'  ? 'border-l-2 border-orange-500'
                  : finding.quantum_risk === 'hybrid'   ? 'border-l-2 border-warning'
                  : ''
                return (
                <Fragment key={finding.id}>
                  <tr
                    className={`cursor-pointer transition-colors hover:bg-container-light dark:hover:bg-container-dark ${rowAccent} ${
                      i % 2 === 1 ? 'bg-background-light dark:bg-background-dark' : ''
                    }`}
                    onClick={() => toggle(finding.id)}
                  >
                    {/* Expand toggle */}
                    <td className="px-3 py-3 text-center">
                      <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">
                        {expandedId === finding.id ? 'expand_less' : 'expand_more'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-text-light dark:text-text-dark">{finding.name}</p>
                        {/* Stale means the monitor has stopped seeing it. It
                            stays in the inventory until the agent retires it,
                            so without a mark here it reads as still present. */}
                        {finding.crypto_properties?._qtz?.status === 'stale' && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-warning/15 text-yellow-800 dark:bg-warning/20 dark:text-yellow-300"
                            title="Not seen recently by the live monitor"
                          >
                            stale
                          </span>
                        )}
                      </div>
                      <code className="text-xs font-mono text-subtle-light dark:text-subtle-dark">
                        {finding.bom_ref}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <AssetTypeBadge assetType={finding.asset_type} />
                    </td>
                    <td className="px-4 py-3">
                      <RiskBadge level={finding.severity} />
                    </td>
                    <td className="px-4 py-3">
                      <QuantumRiskBadge risk={finding.quantum_risk} />
                    </td>
                    <td className="px-4 py-3">
                      <QuantumSafeBadge isSafe={finding.is_quantum_safe} />
                    </td>
                    <td className="px-4 py-3">
                      <ModalityBadge modality={finding.modality as Modality} />
                    </td>
                    {showSourceColumn && (
                      <td className="px-4 py-3 text-xs">
                        {sourceMap[finding.source_id] ? (
                          <Link
                            to={`/sources/${finding.source_id}`}
                            className="text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {sourceMap[finding.source_id]}
                          </Link>
                        ) : (
                          <span className="font-mono text-subtle-light dark:text-subtle-dark">
                            {finding.source_id.slice(0, 8)}…
                          </span>
                        )}
                      </td>
                    )}
                    {showScanColumn && (
                      <td className="px-4 py-3">
                        <Link
                          to={`/scans/${finding.scan_job_id}`}
                          className="font-mono text-xs text-primary hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {finding.scan_job_id.slice(0, 8)}…
                        </Link>
                      </td>
                    )}
                    <td className="px-4 py-3 text-xs text-subtle-light dark:text-subtle-dark max-w-xs break-all">
                      {finding.evidence?.occurrences?.[0]?.location ? (
                        <span className="font-mono">
                          {finding.evidence.occurrences[0].location}
                          {finding.evidence.occurrences[0].line != null && (
                            <span className="text-primary">:{finding.evidence.occurrences[0].line}</span>
                          )}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>

                  {/* Expanded detail row — inlay well */}
                  {expandedId === finding.id && (
                    <tr className="bg-container-light dark:bg-container-dark">
                      <td />
                      <td colSpan={7 + (showSourceColumn ? 1 : 0) + (showScanColumn ? 1 : 0)} className="px-4 py-4">
                        <CryptoPropsDetail finding={finding} />
                      </td>
                    </tr>
                  )}
                </Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
